import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import KeyData from "@/components/KeyData";
import { ChevronDown } from "lucide-react";

export default function RevealMission() {
  const navigate = useNavigate();
  const [m, setM] = useState(null);
  const [showDetail, setShowDetail] = useState(false);
  const [accepting, setAccepting] = useState(false);

  useEffect(() => {
    api.post("/reveal/generate").then(r => setM(r.data.mission)).catch(() => {});
  }, []);

  const accept = async () => {
    setAccepting(true);
    try {
      await api.post("/reveal/accept-mission");
      navigate("/app/today", { replace: true });
    } finally {
      setAccepting(false);
    }
  };

  if (!m) return <div className="min-h-screen flex items-center justify-center"><span className="animate-soft-pulse" style={{ color: "var(--vedari-ink-soft)" }}>A moment…</span></div>;

  return (
    <div className="min-h-screen px-6 py-10 max-w-md mx-auto" style={{ color: "var(--vedari-ink)" }}>
      <p className="text-caption tracking-[0.25em] uppercase" style={{ color: "var(--vedari-gold)" }}>Your First Mission</p>
      <h1 className="font-display text-3xl mt-3 leading-tight">This chapter has one main thing to ask of you</h1>

      <div className="vedari-card mt-8">
        <h2 className="font-display text-[36px] leading-tight mb-2" data-testid="mission-name">{m.name}</h2>
        <p className="text-body-lg leading-relaxed" style={{ color: "var(--vedari-ink-soft)" }}>
          {m.completion_definition}
        </p>

        <div className="flex items-center gap-6 mt-6 pt-6" style={{ borderTop: "1px solid var(--vedari-divider)" }}>
          <div>
            <p className="text-caption uppercase tracking-wider" style={{ color: "var(--vedari-ink-soft)" }}>Duration</p>
            <p className="mt-1"><KeyData>{m.duration_days}</KeyData> days</p>
          </div>
          <div>
            <p className="text-caption uppercase tracking-wider" style={{ color: "var(--vedari-ink-soft)" }}>Sub-tasks</p>
            <p className="mt-1"><KeyData>{m.sub_tasks.length}</KeyData> milestones</p>
          </div>
        </div>

        <div className="mt-6 pt-5" style={{ borderTop: "1px solid var(--vedari-divider)" }}>
          <p className="text-caption uppercase tracking-wider mb-2" style={{ color: "var(--vedari-gold)" }}>Why this for this phase</p>
          <p className="text-body leading-relaxed">{m.why_for_user}</p>
        </div>

        <button onClick={() => setShowDetail(s => !s)} className="flex items-center gap-2 mt-6 text-body-sm" style={{ color: "var(--vedari-slate)" }} data-testid="mission-tellmore-btn">
          {showDetail ? "Hide details" : "Tell me more"}
          <ChevronDown size={16} className={`transition-transform ${showDetail ? "rotate-180" : ""}`} />
        </button>

        {showDetail && (
          <div className="mt-4 space-y-3 pt-4" style={{ borderTop: "1px solid var(--vedari-divider)" }}>
            <p className="text-body leading-relaxed">{m.vedic_basis}</p>
            <p className="text-caption uppercase tracking-wider pt-3" style={{ color: "var(--vedari-ink-soft)" }}>Sub-tasks</p>
            <ol className="space-y-2 text-body-sm">
              {m.sub_tasks.map((st, i) => (
                <li key={st.id} className="flex gap-3">
                  <span className="key-data">{String(i+1).padStart(2, "0")}</span>
                  <span>{st.title}</span>
                </li>
              ))}
            </ol>
          </div>
        )}
      </div>

      <button onClick={accept} disabled={accepting} className="vedari-btn-primary w-full mt-8" data-testid="mission-accept-btn">
        {accepting ? "Beginning…" : "Accept Mission"}
      </button>
    </div>
  );
}
